/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { action, computed, get } from '@ember/object';
import { setDiff, filterBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import SubjectType from '@linc-technologies/linced-common/enums/subject-type';
import {
  findBy,
  resolved,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class SetController extends Controller {
  // Services
  @service
  paperToaster;

  @service
  session;

  @service
  store;

  @service
  toaster;

  ProgressionType = ProgressionType;

  @computed('model.set.progressionType')
  get isOTJ() {
    return this.model.set.progressionType === ProgressionType.OTJ;
  }

  @filterBy('model.pages', 'hasOTJAssessment', true)
  otjPages;

  @tracked
  activeSubject;

  @action
  setActiveSubject(value) {
    this.activeSubject = value;
  }

  @tracked
  selectedTab = 'expectations';

  @action
  setSelectedTab(value) {
    this.selectedTab = value;
  }

  milestoneTypes = Object.freeze([
    { value: 0, name: 'Unspecified' },
    { value: 1, name: 'Weeks at School' },
    { value: 2, name: 'Months at School' },
    { value: 3, name: 'Year Level' },
  ]);

  @tracked
  milestoneDialogOpen = false;

  @computed('activeSubject.id', 'model.goalCategories.[]')
  get subjectCategories() {
    let subjectId = this.get('activeSubject.id');
    if (isEmpty(subjectId)) {
      return [];
    } else {
      return this.get('model.goalCategories')
        .filter((category) => get(category, 'subject.id') === subjectId)
        .sort(
          (a, b) =>
            a.sequence - b.sequence ||
            (a.label || '').localeCompare(b.label || ''),
        );
    }
  }

  @computed('isOTJ', 'activeSubject.id', 'model.levels.[]')
  get subjectLevels() {
    let subjectId = this.get('activeSubject.id');
    if (isEmpty(subjectId)) {
      return [];
    } else {
      let filter = 'goalSubjectTemplate.id';
      if (this.isOTJ) {
        filter = 'pageTemplate.id';
      }
      return this.get('model.levels')
        .filter((level) => level.get(filter) === subjectId)
        .sort(
          (a, b) =>
            a.sequence - b.sequence ||
            (a.label || '').localeCompare(b.label || ''),
        );
    }
  }

  @computed('isOTJ', 'activeSubject.id', 'model.expectations.[]')
  get subjectExpectations() {
    let isOTJ = this.isOTJ;
    let activeSubjectId = this.get('activeSubject.id');
    return this.get('model.expectations').filter((e) =>
      isOTJ
        ? get(e, 'pageTemplate.id') === activeSubjectId
        : get(e, 'goalSubjectTemplate.id') === activeSubjectId,
    );
  }

  @computed('activeSubject.subjectType')
  get subjectType() {
    if (
      isEmpty(this.activeSubject) ||
      isEmpty(this.activeSubject.subjectType)
    ) {
      // Return the default/unset "Goals" subject type.
      return SubjectType.getObject(0);
    }
    return SubjectType.getObject(this.activeSubject.subjectType);
  }

  unsetMarkers = Object.freeze([
    { label: 'below' },
    { label: 'at' },
    { label: 'above' },
    { label: 'wellAbove' },
  ]);

  @computed(
    'activeSubject.id',
    'model.{levels.[],milestones.[],expectations.[]}',
  )
  get markers() {
    let milestones = this.get('model.milestones');
    let markers = [];
    let unsetMarkers = this.unsetMarkers;
    let numLevels = this.get('subjectLevels.length');
    let subjectExpectations = this.subjectExpectations;

    milestones.forEach((milestone) => {
      let milestoneMarkers = [];
      milestoneMarkers.__milestoneId__ = milestone.id;

      for (let i = -1; i < numLevels; i++) {
        let markerList = [];
        markerList.__pos__ = i;
        markerList.__milestoneId__ = milestone.id;
        milestoneMarkers.push(markerList);
      }

      let expectation = subjectExpectations.find(
        (e) => e.milestone_template_id === milestone.id,
      );
      if (!isEmpty(expectation)) {
        unsetMarkers.forEach((marker) => {
          let index = expectation.get(`${marker.label}Index`);
          findBy(milestoneMarkers, '__pos__', index).push(marker);
        });
      } else {
        let markerList = milestoneMarkers[0];
        unsetMarkers.forEach((marker) => markerList.push(marker));
      }

      markers.push(milestoneMarkers);
    });

    return markers;
  }

  @tracked
  levelDialogOpen = false;

  @tracked
  includeSubjectsWithLevels = true;

  @computed('model.levels.@each.goal_subject_template_id')
  get subjectsWithLevels() {
    return [
      ...new Set(
        this.model.levels.map((level) => level.goal_subject_template_id),
      ),
    ];
  }

  // Usage sets (for filtering Subjects).
  @tracked
  selectedUsageSetsFilter = [];

  @setDiff('model.usageSets', 'selectedUsageSetsFilter')
  remainingUsageSetsFilter;

  @computed(
    'model.subjects.[]',
    'selectedUsageSetsFilter.[]',
    'includeSubjectsWithLevels',
    'subjectsWithLevels.[]',
  )
  get filteredSubjects() {
    if (isEmpty(this.selectedUsageSetsFilter)) {
      return this.model.subjects
        .slice()
        .sort(
          (a, b) =>
            a.sequence - b.sequence ||
            (a.label || '').localeCompare(b.label || ''),
        );
    }
    return this.get('model.subjects')
      .filter((subject) => {
        let usageSetMatches = this.selectedUsageSetsFilter.some((usageSet) =>
          subject.get('usageSetsArray').includes(usageSet),
        );
        let hasLevels = this.subjectsWithLevels.includes(subject.id);
        return usageSetMatches || (this.includeSubjectsWithLevels && hasLevels);
      })
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  // Milestone Sets.
  @alias('model.milestoneSets')
  milestoneSetOptions;

  @computed('selectedMilestoneSet', 'model.{set,allSets.[]}')
  get otherTemplatesWithMilestoneSet() {
    if (this.selectedMilestoneSet.isDefault) {
      // Default milestones don't get shared between Template Sets.
      return [];
    }
    let selectedMilestoneSetId = this.get('selectedMilestoneSet.id');
    let currentSet = this.model.set;
    return this.model.allSets.filter(
      (set) =>
        set.milestone_set_template_id === selectedMilestoneSetId &&
        set !== currentSet,
    );
  }

  @tracked
  selectedMilestoneSet = null;

  @tracked
  milestoneSetDialogOpen = false;

  @tracked
  milestoneSet = null;

  @tracked
  milestoneSetLabel = '';

  @action
  setMilestoneSetLabel(value) {
    this.milestoneSetLabel = value;
  }

  @tracked
  milestoneSetSaveInProgress = false;

  @tracked
  deleteDialogOpen;

  @tracked
  deleteInProgress;

  @tracked
  deleteLabel;

  @tracked
  deleteType;

  @tracked
  errorMessage;

  @tracked
  levelBriefLabel;

  @action
  setLevelBriefLabel(value) {
    this.levelBriefLabel = value;
  }

  @tracked
  levelGoalCategoryTemplate;

  @action
  setLevelGoalCategoryTemplate(value) {
    this.levelGoalCategoryTemplate = value;
  }

  @tracked
  levelId;

  @tracked
  levelLabel;

  @action
  setLevelLabel(value) {
    this.levelLabel = value;
  }

  @tracked
  levelSaveInProgress;

  @tracked
  milestoneDay;

  @action
  setMilestoneDay(value) {
    this.milestoneDay = value;
  }

  @tracked
  milestoneDuration;

  @action
  setMilestoneDuration(value) {
    this.milestoneDuration = value;
  }

  @tracked
  milestoneId;

  @tracked
  milestoneIsInterim;

  @action
  setMilestoneIsInterim(value) {
    this.milestoneIsInterim = value;
  }

  @tracked
  milestoneLabel;

  @action
  setMilestoneLabel(value) {
    this.milestoneLabel = value;
  }

  @tracked
  milestoneMonth;

  @action
  setMilestoneMonth(value) {
    this.milestoneMonth = value;
  }

  @tracked
  milestoneType;

  @action
  setMilestoneType(value) {
    this.milestoneType = value;
  }

  @tracked
  milestoneYearLevel;

  @action
  setMilestoneYearLevel(value) {
    this.milestoneYearLevel = value;
  }

  @tracked
  saveInProgress;

  @computed(
    'selectedMilestoneSet.{id,milestones.content.[]}',
    'model.{milestones.[],milestoneSets.@each.label}',
  )
  get milestones() {
    if (isEmpty(this.get('selectedMilestoneSet.id'))) {
      // No Milestone Set / default milestones selected.
      return this.model.milestones;
    }
    return resolved(this.selectedMilestoneSet.milestones);
  }

  @action
  closeMilestoneSetDialog() {
    this.milestoneSet = null;
    this.milestoneSetLabel = '';
    this.milestoneSetDialogOpen = false;
    this.milestoneSetSaveInProgress = false;
  }

  @action
  addMilestone() {
    this.resetMilestoneForm();
    this.milestoneDialogOpen = true;
  }

  @action
  editMilestone(milestone) {
    this.milestoneId = milestone.get('id');
    this.milestoneLabel = milestone.get('label');
    this.milestoneType = findBy(
      this.milestoneTypes,
      'value',
      milestone.get('type'),
    );
    this.milestoneDuration = milestone.get('duration');
    this.milestoneYearLevel = milestone.get('yearLevel');
    this.milestoneMonth = milestone.get('month');
    this.milestoneDay = milestone.get('day');
    this.milestoneIsInterim = milestone.get('isInterim');
    this.milestoneDialogOpen = true;
  }

  @action
  closeMilestoneDialog() {
    this.milestoneDialogOpen = false;
  }

  @action
  saveMilestone() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;

    if (isEmpty(this.milestoneId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('milestones/milestone', {
          label: this.milestoneLabel,
          sequence: this.get('model.milestones.length') + 1,
          type: this.get('milestoneType.value'),
          duration: this.milestoneDuration,
          yearLevel: this.milestoneYearLevel,
          month: this.milestoneMonth,
          day: this.milestoneDay,
          templateSetId: this.get('model.set'),
          isInterim: this.milestoneIsInterim,
          progressionType: this.model.set.progressionType,
        });
        if (!this.selectedMilestoneSet.isDefault) {
          record.set('milestoneSetTemplateId', this.selectedMilestoneSet);
        }

        resolve(record);
      });
    } else {
      promise = new Promise((resolve) => {
        const id = this.milestoneId;
        this.store.findRecord('milestones/milestone', id).then((milestone) => {
          milestone.set('label', this.milestoneLabel);
          milestone.set('type', this.get('milestoneType.value'));
          milestone.set('duration', this.milestoneDuration);
          milestone.set('yearLevel', this.milestoneYearLevel);
          milestone.set('month', this.milestoneMonth);
          milestone.set('day', this.milestoneDay);
          milestone.set('isInterim', this.milestoneIsInterim);

          resolve(milestone);
        });
      });
    }

    promise.then((record) => {
      let toaster = this.paperToaster;

      return record
        .save()
        .then(() => {
          this.milestoneDialogOpen = false;
          this.resetMilestoneForm();
          toaster.show('Milestone saved successfully.', {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          });
          this.get('model.milestones').update();
        })
        .catch(() => {
          record.rollbackAttributes();
          toaster.show('Error saving Milestone.', {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          });
        })
        .then(() => {
          this.saveInProgress = false;
        });
    });
  }

  @action
  deleteMilestone(milestone) {
    this.errorMessage = '';
    this.deleteLabel = milestone.get('label');
    this.milestoneId = milestone.id;
    this.deleteType = 'Milestone';
    this.deleteDialogOpen = true;
  }

  @action
  addLevel() {
    this.resetLevelForm();
    this.levelDialogOpen = true;
  }

  @action
  async editLevel(level) {
    this.levelId = level.get('id');
    this.levelLabel = level.get('label');
    this.levelBriefLabel = level.get('briefLabel');
    // A belongsTo takes a record, not the async proxy.
    this.levelGoalCategoryTemplate = await level.goalCategoryTemplate;
    this.levelDialogOpen = true;
  }

  @action
  closeLevelDialog() {
    this.levelDialogOpen = false;
  }

  @action
  saveLevel() {
    this.levelSaveInProgress = true;
    this.errorMessage = '';
    let promise;

    if (isEmpty(this.levelId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('milestones/level', {
          label: this.levelLabel,
          briefLabel: this.levelBriefLabel,
          sequence: this.get('subjectLevels.length') + 1,
          templateSetId: this.get('model.set'),
          progressionType: this.model.set.progressionType,
        });

        if (this.isOTJ) {
          record.set('pageTemplateId', this.activeSubject);
        } else {
          record.set('goalSubjectTemplateId', this.activeSubject);
          record.set('goalCategoryTemplateId', this.levelGoalCategoryTemplate);
        }

        resolve(record);
      });
    } else {
      promise = new Promise((resolve) => {
        const id = this.levelId;
        this.store.findRecord('milestones/level', id).then((level) => {
          level.set('label', this.levelLabel);
          level.set('briefLabel', this.levelBriefLabel);

          if (!this.isOTJ) {
            level.set('goalCategoryTemplateId', this.levelGoalCategoryTemplate);
          }

          resolve(level);
        });
      });
    }

    promise.then((record) => {
      let toaster = this.paperToaster;

      return record
        .save()
        .then(() => {
          this.levelDialogOpen = false;
          this.resetLevelForm();
          toaster.show('Level saved successfully.', {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          });
          this.get('model.levels').update();
        })
        .catch(() => {
          // rollbackAttributes() alone would leave goalCategoryTemplateId.
          rollbackRecord(record);
          toaster.show('Error saving Level.', {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          });
        })
        .then(() => {
          this.levelSaveInProgress = false;
        });
    });
  }

  @action
  deleteLevel(level) {
    this.errorMessage = '';
    this.deleteLabel = level.get('label');
    this.levelId = level.id;
    this.deleteType = 'Level';
    this.deleteDialogOpen = true;
  }

  // Generic delete handler for all entity types
  @action
  confirmDelete(confirmed) {
    let toaster = this.paperToaster;
    let type = this.deleteType;
    let typeLower = type.toLowerCase();

    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let entity = this.store.peekRecord(
          `milestones/${typeLower}`,
          this.get(`${typeLower}Id`),
        );

        // Delete record (locally + pushed to server).
        return entity
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            entity.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteDialogOpen = false;
        this[`reset${type}Form`]();
        if (confirmed) {
          toaster.show(`${type} deleted successfully.`, {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          });
        }
      })
      .catch(() => {
        toaster.show(`Error deleting ${type}.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    targetList.forEach((entity, index) => {
      entity.set('sequence', index + 1);
      entity.save();
    });
  }

  // drag-drop sorting for expectations
  @action
  setExpectation({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    let expectation = this.subjectExpectations.find(
      (e) => e.milestone_template_id === targetList.__milestoneId__,
    );
    if (isEmpty(expectation)) {
      expectation = this.store.createRecord('milestones/expectation', {
        milestoneTemplateId: this.get('model.milestones').find(
          (m) => m.id === targetList.__milestoneId__,
        ),
        templateSetId: this.get('model.set'),
        progressionType: this.model.set.progressionType,

        // Set all indices to -1 - we'll adjust them (where applicable) before saving.
        belowIndex: -1,
        atIndex: -1,
        aboveIndex: -1,
        wellAboveIndex: -1,
      });

      if (this.isOTJ) {
        expectation.set('pageTemplateId', this.activeSubject);
      } else {
        expectation.set('goalSubjectTemplateId', this.activeSubject);
      }
    }

    // Set new expectation level for dragged handle
    let newPos = targetList.__pos__;
    expectation.set(`${item.label}Index`, newPos);

    // If any higher levels are now below this one, bring them up
    let targets = this.unsetMarkers;
    let target = targets.indexOf(item);
    for (let i = 0; i < target; i++) {
      let key = `${targets[i].label}Index`;
      if (expectation.get(key) > newPos) {
        expectation.set(key, newPos);
      }
    }
    for (let i = target + 1; i < targets.length; i++) {
      let key = `${targets[i].label}Index`;
      if (expectation.get(key) < newPos) {
        expectation.set(key, newPos);
      }
    }

    // Save the updated expectation record
    expectation
      .save()
      .then(() => {
        this.get('model.expectations').update();
      })
      .catch(() => {
        // Unload a new record and revert an edited one, so the next drag
        // starts from what the server holds.
        rollbackRecord(expectation);
        this.toaster.error('Error saving expectation.');
      });
  }

  @action
  calculateLevelClass(index, expectation) {
    if (isEmpty(expectation)) {
      return;
    }
    if (index <= expectation.get('belowIndex')) {
      return 'well-below';
    } else if (index <= expectation.get('atIndex')) {
      return 'below';
    } else if (index <= expectation.get('aboveIndex')) {
      return 'at';
    } else if (index <= expectation.get('wellAboveIndex')) {
      return 'above';
    } else {
      return 'well-above';
    }
  }

  @action
  addUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = [...this.selectedUsageSetsFilter, usageSet];
  }

  @action
  removeUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = this.selectedUsageSetsFilter.filter(
      (u) => u !== usageSet,
    );
  }

  @action
  editMilestoneSet(milestoneSet) {
    this.milestoneSet = milestoneSet;
    this.milestoneSetLabel = milestoneSet.label;
    this.milestoneSetDialogOpen = true;
  }

  @action
  addMilestoneSet() {
    this.milestoneSet = null;
    this.milestoneSetLabel = '';
    this.milestoneSetDialogOpen = true;
  }

  @action
  selectMilestoneSet(newSet) {
    const previousSet = this.selectedMilestoneSet;
    this.selectedMilestoneSet = newSet;
    if (newSet.isDefault) {
      this.model.set.set('milestoneSetTemplateId', null);
    } else {
      this.model.set.set('milestoneSetTemplateId', newSet);
    }
    return this.model.set
      .save()
      .then(() => {
        this.model.milestones.update();
      })
      .catch(() => {
        // Go back to the milestone set the server still has.
        rollbackRecord(this.model.set);
        this.selectedMilestoneSet = previousSet;
        this.paperToaster.show('Error saving the milestone set.', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      });
  }

  @action
  saveMilestoneSet() {
    this.milestoneSetSaveInProgress = true;
    let promise = Promise.resolve();
    if (isEmpty(this.milestoneSet)) {
      let record = this.store.createRecord('milestones/milestone-set', {
        label: this.milestoneSetLabel,
      });
      promise = record.save().then((newSet) => (this.milestoneSet = newSet));
    } else if (isEmpty(this.milestoneSet.id)) {
      // Converting the default milestones into a named Set.
      let record = this.store.createRecord('milestones/milestone-set', {
        label: this.milestoneSetLabel,
      });
      promise = record
        .save()
        .then((newSet) => {
          // Store the new Milestone Set for later steps.
          this.milestoneSet = newSet;

          // Move all default milestones to the new Set.
          return this.milestones.reduce((promiseChain, milestone) => {
            milestone.setProperties({
              templateSetId: null,
              milestoneSetTemplateId: newSet,
            });
            return promiseChain.then(() => milestone.save());
          }, Promise.resolve());
        })
        .then(() => {
          this.model.set.set('milestoneSetTemplateId', this.milestoneSet);
          this.selectedMilestoneSet = this.milestoneSet;
          return this.model.set.save();
        })
        .catch(() => {
          // Something failed.  Revert all changes and delete the new Set.
          // Revert milestones to be the default milestones.
          this.selectedMilestoneSet = findBy(
            this.milestoneSetOptions,
            'isDefault',
          );
          let revertChain = resolved(this.milestoneSet.milestones).reduce(
            (promiseChain, milestone) => {
              milestone.setProperties({
                templateSetId: this.model.set,
                milestoneSetTemplateId: null,
              });
              return promiseChain.then(() => milestone.save());
            },
            Promise.resolve(),
          );

          // Delete the Set that was created.
          revertChain = revertChain
            .then(() => {
              this.model.set.set('milestoneSetTemplateId', null);
              return this.model.set.save();
            })
            .then(() => {
              this.milestoneSet.destroyRecord();
              this.milestoneSet = null;
            });

          // Reject so that the generic `catch()` below triggers a toast.
          return revertChain.finally(() => Promise.reject());
        });
    } else {
      this.milestoneSet.set('label', this.milestoneSetLabel);
      promise = this.milestoneSet.save();
    }
    promise
      .then(() => {
        if (
          isEmpty(findBy(this.milestoneSetOptions, 'id', this.milestoneSet.id))
        ) {
          // New Milestone Set added.
          // Push it into the list of options, but don't select it.
          this.milestoneSetOptions = [
            ...this.milestoneSetOptions,
            this.milestoneSet,
          ];
          // Grab the "Default Milestones" virtual Milestone Set.
          let defaultMilestones = findBy(this.milestoneSetOptions, 'isDefault');
          if (!isEmpty(defaultMilestones)) {
            this.milestoneSetOptions = sortBy(
              this.milestoneSetOptions.filter((m) => m !== defaultMilestones),
              'label',
            );
            this.milestoneSetOptions = [
              defaultMilestones,
              ...this.milestoneSetOptions,
            ];
          }
        }
      })
      .catch(() => {
        this.toaster.error('Error saving Milestone Set Template!');
      })
      .finally(() => {
        this.closeMilestoneSetDialog();
        this.milestoneSetSaveInProgress = false;
      });
  }

  resetMilestoneForm() {
    this.milestoneId = null;
    this.milestoneLabel = '';
    this.milestoneType = null;
    this.milestoneDuration = 0;
    this.milestoneYearLevel = 0;
    this.milestoneMonth = 0;
    this.milestoneDay = 0;
    this.milestoneIsInterim = false;
  }

  resetLevelForm() {
    this.levelId = null;
    this.levelLabel = '';
    this.levelBriefLabel = '';
    this.levelGoalCategoryTemplate = null;
  }
}
